/**
 * OAuth callback page for Email account connection.
 *
 * Handles the redirect from Google/Microsoft, exchanges the code via the api
 * client, notifies the opener via BroadcastChannel, then closes the window.
 * Mirrors the calendar arche's SyncCallbackPage.
 */

import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { EMAIL_OAUTH_CHANNEL } from '../hooks/useConnectAccount';

export function ConnectCallbackPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      setStatus('error');
      setErrorMessage(error === 'access_denied' ? 'Access was denied.' : error);
      return;
    }
    if (!code || !state) {
      setStatus('error');
      setErrorMessage('Missing authorization code or state.');
      return;
    }

    api
      .emailConnectionOAuthCallback(code, state)
      .then(() => {
        setStatus('success');
        const channel = new BroadcastChannel(EMAIL_OAUTH_CHANNEL);
        channel.postMessage({ type: 'oauth-complete' });
        channel.close();
        setTimeout(() => window.close(), 1500);
      })
      .catch((err) => {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Failed to connect mailbox.');
      });
  }, [searchParams]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center space-y-4 p-8">
        {status === 'loading' && (
          <>
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground">Connecting your mailbox...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="h-8 w-8 mx-auto text-green-500" />
            <p className="font-medium">Mailbox connected!</p>
            <p className="text-sm text-muted-foreground">This window will close automatically.</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="h-8 w-8 mx-auto text-destructive" />
            <p className="font-medium">Connection failed</p>
            <p className="text-sm text-muted-foreground">{errorMessage}</p>
          </>
        )}
      </div>
    </div>
  );
}
